/**
 * Hyetech sticky add-to-cart bar.
 * Shows a fixed bottom bar once the user scrolls past the product hero
 * (the <product-info> custom element that wraps the whole hero section),
 * and automatically hides it again once the site footer scrolls into
 * view so it can never sit on top of footer content. Reuses the
 * existing, already-wired hero controls (Add to Cart / Buy Now
 * buttons, quantity input) instead of duplicating cart logic, so cart
 * drawer/notification behaviour stays consistent.
 *
 * Visibility is driven purely by the "is-visible" class (toggled here);
 * the actual show/hide animation (opacity/transform/visibility fade +
 * slide) lives entirely in assets/hyetech-product-page.css. This script
 * no longer touches the "hidden" attribute at all - the bar's resting
 * (not-yet-shown) state is defined in CSS, so there's no flash of the
 * bar before this script runs.
 */
(function () {
  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    var hero = document.querySelector('product-info');
    var bars = document.querySelectorAll('[data-hy-sticky-cart]');
    if (!hero || !bars.length || !('IntersectionObserver' in window)) return;

    // The theme always renders the footer section as a real <footer
    // class="footer"> element (sections/footer.liquid, included via
    // {% sections 'footer-group' %} in layout/theme.liquid) - that's an
    // existing, stable selector, so no new id/attribute needed on the
    // footer itself.
    var footer = document.querySelector('footer.footer');

    var heroScrolledPast = false;
    var footerVisible = false;

    // Single source of truth for whether each bar should be showing.
    // Both observers below just update their own flag and call this -
    // that keeps the "hero scrolled past" state, the "footer visible"
    // state and each bar's own dismissed flag from ever fighting each
    // other or applying the [hidden]/class toggle out of order.
    function updateBars() {
      var shouldShow = heroScrolledPast && !footerVisible;
      bars.forEach(function (bar) {
        if (shouldShow && !bar.hyDismissed) {
          bar.classList.add('is-visible');
        } else {
          bar.classList.remove('is-visible');
        }
      });
    }

    var heroObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          heroScrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        });
        updateBars();
      },
      { threshold: 0, rootMargin: '0px' }
    );
    heroObserver.observe(hero);

    if (footer) {
      var footerObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            footerVisible = entry.isIntersecting;
          });
          updateBars();
        },
        {
          threshold: 0,
          // Grow the effective viewport 150px past its real bottom
          // edge, so the footer counts as "visible" a little before it
          // actually reaches the bottom of the screen. That gives the
          // bar's 300ms fade-out transition (hyetech-product-page.css)
          // time to finish before the footer would otherwise appear
          // from underneath it - no visible overlap moment.
          rootMargin: '0px 0px 150px 0px',
        }
      );
      footerObserver.observe(footer);
    }

    bars.forEach(function (bar) {
      var qtyInput = bar.querySelector('[data-hy-sticky-qty-input]');
      var minusBtn = bar.querySelector('[data-hy-sticky-qty-minus]');
      var plusBtn = bar.querySelector('[data-hy-sticky-qty-plus]');
      var addBtn = bar.querySelector('[data-hy-sticky-add]');
      var buyBtn = bar.querySelector('[data-hy-sticky-buy]');
      var closeBtn = bar.querySelector('[data-hy-sticky-close]');

      if (minusBtn && qtyInput) {
        minusBtn.addEventListener('click', function () {
          var val = parseInt(qtyInput.value, 10) || 1;
          qtyInput.value = Math.max(1, val - 1);
        });
      }
      if (plusBtn && qtyInput) {
        plusBtn.addEventListener('click', function () {
          var val = parseInt(qtyInput.value, 10) || 1;
          qtyInput.value = val + 1;
        });
      }

      function syncHeroQuantity() {
        if (!qtyInput) return;
        var heroQtyInput = document.querySelector('[id^="Quantity-"]');
        if (heroQtyInput) heroQtyInput.value = qtyInput.value;
      }

      if (addBtn) {
        addBtn.addEventListener('click', function () {
          syncHeroQuantity();
          var heroAddBtn = document.querySelector('[id^="ProductSubmitButton-"]');
          if (heroAddBtn) heroAddBtn.click();
        });
      }

      if (buyBtn) {
        buyBtn.addEventListener('click', function () {
          syncHeroQuantity();
          var heroBuyBtn = document.querySelector('[id^="BuyNow-"]');
          if (heroBuyBtn) heroBuyBtn.click();
        });
      }

      if (closeBtn) {
        closeBtn.addEventListener('click', function () {
          // Dismiss just this bar (desktop and mobile bars are separate
          // elements, so each keeps its own independent dismissed state)
          // for the rest of this page view. Plain in-memory flag on the
          // element (no storage), so it resets on the next page load -
          // matches the client's stated preferred behaviour ("if the
          // page is refreshed, the sticky bar can appear again").
          bar.hyDismissed = true;
          bar.classList.remove('is-visible');
        });
      }
    });
  });
})();
